import { SuburbGraph } from '../rules/suburb-map-checks';
import { AreaState, SubRegionAction, applyAreaEdit } from '../rules/suburb-subregion-edits';
import { SuburbShapes } from './map-data';
import { codeAt } from './map-geometry';
import * as queries from './map-queries';
import {
  EngineHooks,
  MapEdit,
  MapMode,
  MapPage,
  MapRegion,
  MapRow,
  MapSubRegion,
  MapTool,
  OVERVIEW,
} from './map-types';

/**
 * The suburb map's state (#256, #260): the loaded suburbs, the local copy of their assignments, the regions and
 * sub-regions, the pages, and the tool settings, with the rules that keep them consistent. No DOM and no deck.gl:
 * SuburbMapEngine extends it with the basemap, the drawing and the pointer tools, and MapEditor (map-editor.ts) edits
 * it. `render()` is the engine's; the bare model has nothing to draw.
 */
export class SuburbMapModel {
  regions: MapRegion[] = [];
  /** salCode -> stored state (optimistic: edits apply here first, then the server's rows are mirrored back). */
  areas = new Map<string, AreaState>();
  subRegions: MapSubRegion[] = [];
  /** Sub-region mode: the one a click or sweep edits, and whether a sweep adds to it or takes out of it. */
  activeSubRegion: string | null = null;
  subRegionAction: SubRegionAction = 'add';
  /**
   * Assign mode's optional sub-region (#260, Ivan 2026-10-21): null = the active region only; else assigned suburbs
   * join this sub-region of it too. A new active region resets it to null.
   */
  assignSubRegion: string | null = null;
  /** Every sub-region's outline (the active one is always drawn in Sub-region mode). */
  showSubRegions = true;
  pages = new Map<string, MapPage>();
  /** The loaded state's suburbs (GeoJSON features), or every assigned one in the overview. */
  features: any[] = [];
  stateCode: string | null = null;
  overview = false;
  /** Editing switched off for the whole map (nothing sets it today; see the workspace's readOnly). */
  readOnly = false;
  mode: MapMode = 'view';
  tool: MapTool = 'pointer';
  /** The suburb whose panel is open, outlined on the map. */
  selectedCode: string | null = null;
  hiddenRegions = new Set<string>();
  /** Sub-regions whose outline the Regions filter turned off (#273); the one being edited is still drawn. */
  hiddenSubRegions = new Set<string>();
  unassignedOnly = false;
  showLabels = true;

  /** Bumped on every change to the assignments, so the layers' colours are recomputed. */
  protected revision = 0;
  protected readonly shapes = new SuburbShapes();
  private _activeRegion: string | null = null;

  constructor(protected hooks: EngineHooks) {}

  get activeRegion(): string | null {
    return this._activeRegion;
  }
  set activeRegion(id: string | null) {
    if (id !== this._activeRegion) this.assignSubRegion = null; // the pick is one of this region's sub-regions
    this._activeRegion = id;
  }

  get editable(): boolean {
    return !this.readOnly && !this.overview;
  }

  /** Assign mode's sub-region, if one is picked (and still one of the active region's). */
  get assignSubRegionRef(): MapSubRegion | undefined {
    const sr = this.subRegionById(this.assignSubRegion);
    return sr && sr.regionId === this.activeRegion ? sr : undefined;
  }

  /** Draw the map again (the engine's; the bare model has nothing to draw). */
  render(): void {}

  /** The assignments changed: recolour, and tell the workspace (counts, lists). */
  markChanged(): void {
    this.revision++;
    this.hooks.onChange();
  }

  // ------------------------------------------------------------------ data from the server

  setData(
    regions: MapRegion[],
    rows: MapRow[],
    pages: Map<string, MapPage>,
    subRegions: MapSubRegion[] = []
  ): void {
    this.regions = regions;
    this.pages = pages;
    this.subRegions = subRegions;
    this.areas = new Map(
      rows
        .filter((r) => r.regionId)
        .map((r) => [
          r.salCode,
          {
            regionId: r.regionId as string,
            covered: !!r.covered,
            subRegions: [...(r.subRegions ?? [])],
          },
        ])
    );
    this.keepActiveRegion();
    this.keepActiveSubRegion();
    this.markChanged();
  }

  /** Regions changed (added, renamed): keep the assignments, recolour. */
  setRegions(regions: MapRegion[]): void {
    this.regions = regions;
    this.keepActiveRegion();
    this.render();
    this.markChanged();
  }

  /** Sub-regions changed (added, renamed, recoloured, deleted): keep the memberships, redraw. */
  setSubRegions(subRegions: MapSubRegion[]): void {
    this.subRegions = subRegions;
    this.keepActiveSubRegion();
    this.render();
    this.markChanged();
  }

  setPages(pages: Map<string, MapPage>): void {
    this.pages = pages;
    this.render();
  }

  /** Mirror rows the server stored (after a save, an undo or a conflict). */
  applyRows(rows: MapRow[]): void {
    for (const r of rows) {
      if (r.regionId)
        this.areas.set(r.salCode, {
          regionId: r.regionId,
          covered: !!r.covered,
          subRegions: [...(r.subRegions ?? [])],
        });
      else this.areas.delete(r.salCode);
    }
    if (this.overview)
      this.features = this.features.filter((f) => this.areas.has(f.properties.code));
    this.render();
    this.markChanged();
  }

  /** As the server stores it: a new region (or none) drops the suburb's memberships; joining puts it in the region. */
  applyEdit(e: MapEdit): void {
    applyAreaEdit(this.areas, e, (id) => this.subRegionById(id)?.regionId);
  }

  /** A suburb as it stands, to put back if a save fails. */
  rowOf(code: string): MapRow {
    const r = this.areas.get(code);
    return {
      salCode: code,
      regionId: r?.regionId ?? null,
      covered: !!r?.covered,
      subRegions: [...(r?.subRegions ?? [])],
    };
  }

  select(code: string | null): void {
    this.selectedCode = code;
    this.render();
  }

  regionById(id: string | null | undefined): MapRegion | undefined {
    return id ? this.regions.find((r) => r.id === id) : undefined;
  }

  subRegionById(id: string | null | undefined): MapSubRegion | undefined {
    return id ? this.subRegions.find((s) => s.id === id) : undefined;
  }

  private keepActiveRegion(): void {
    if (!this.activeRegion || !this.regions.some((r) => r.id === this.activeRegion))
      this.activeRegion = this.regions[0]?.id ?? null;
  }

  private keepActiveSubRegion(): void {
    if (this.activeSubRegion && !this.subRegions.some((s) => s.id === this.activeSubRegion))
      this.activeSubRegion = null;
    if (!this.activeSubRegion) this.activeSubRegion = this.subRegions[0]?.id ?? null;
    if (
      this.assignSubRegion &&
      this.subRegionById(this.assignSubRegion)?.regionId !== this.activeRegion
    )
      this.assignSubRegion = null;
  }

  // ------------------------------------------------------------------ loading a state

  /** Load one state's suburbs (or, for OVERVIEW, every assigned suburb in every state) and show them. */
  async loadState(code: string): Promise<void> {
    if (code === OVERVIEW) return this.loadOverview();
    this.hooks.onStatus(`Loading ${code.toUpperCase()}…`);
    this.stateCode = code;
    this.overview = false;
    this.features = await this.shapes.featuresFor(code);
    this.showLoaded();
    this.hooks.onStatus(
      `${this.features.length.toLocaleString()} suburbs in ${code.toUpperCase()}.`
    );
  }

  /** Every assigned suburb across all states, at a glance. Read-only: a click would be ambiguous about the state. */
  private async loadOverview(): Promise<void> {
    this.hooks.onStatus('Loading all states…');
    const all = await this.allFeatures();
    this.stateCode = OVERVIEW;
    this.overview = true;
    this.features = all.filter((f) => this.areas.has(f.properties.code));
    this.showLoaded();
    this.hooks.onStatus(
      `Overview: ${this.features.length.toLocaleString()} assigned suburbs, read-only. Pick a state to edit.`
    );
  }

  /** A state was loaded (the engine also fits the map to it and arms its tools). */
  protected showLoaded(): void {
    this.render();
    this.markChanged();
  }

  // ------------------------------------------------------------------ shapes and questions (map-data.ts, map-queries.ts)

  /** Every state's features (for exports). Cached after the first load. */
  allFeatures(): Promise<any[]> {
    return this.shapes.allFeatures();
  }

  graph(): Promise<SuburbGraph> {
    return this.shapes.graph();
  }

  /** The suburb a point is in, from its state's shapes (loaded on demand), or null (a place moved to a picked address). */
  async suburbAt(lat: number, lng: number, state: string): Promise<string | null> {
    return codeAt(await this.shapes.featuresFor(state.toLowerCase()), lng, lat);
  }

  visibleFeatures(): any[] {
    return queries.visibleFeatures(
      this.features,
      this.areas,
      this.hiddenRegions,
      this.unassignedOnly
    );
  }

  visibleCodes(): Set<string> {
    return new Set(this.visibleFeatures().map((f) => f.properties.code));
  }

  subRegionCounts(): Map<string, number> {
    return queries.subRegionCounts(this.areas);
  }

  regionCounts(): Map<string, queries.RegionTally> {
    return queries.regionCounts(this.areas);
  }

  inLoadedState(): { assigned: number; total: number } {
    return queries.inLoadedState(this.features, this.areas);
  }

  /** A drawn suburb's ABS name ('' when it isn't in the loaded state). */
  areaName(code: string): string {
    return queries.areaName(this.features, code);
  }

  search(q: string, limit?: number): queries.SearchHit[] {
    return queries.searchFeatures(this.features, this.areas, q, limit);
  }

  searchByCodes(codes: readonly string[]): queries.SearchHit[] {
    return queries.searchByCodes(this.features, this.areas, codes);
  }
}
